import clsx from 'clsx';
import React, { type FC, useCallback, useMemo, useState } from 'react';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { CodeBlock } from '@fragment/ui/src/components/CodeBlock/CodeBlock';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

type ApiClientCreatedProps = {
  clientId: string;
  clientSecret: string;
};

export const ApiClientCreatedView: FC<ApiClientCreatedProps> = ({
  clientId,
  clientSecret,
}) => {
  const [activeTab, setActiveTab] = useState<'json' | 'cli'>('json');
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  const data = useMemo(
    () => ({
      clientId,
      clientSecret,
      apiUrl: activeWorkspaceFromContext?.pod.customerApiUrl ?? '',
      oauthUrl: `https://${activeWorkspaceFromContext?.pod.cognitoAuthDomain}/oauth2/token`,
      oauthScope: activeWorkspaceFromContext?.pod.customerApiOAuthScope ?? '',
    }),
    [clientId, clientSecret, activeWorkspaceFromContext]
  );

  const cliLoginCommand = useMemo(() => {
    const { apiUrl, oauthUrl, oauthScope } = data;
    return `fragment login \\
    --client-id ${clientId} \\
    --client-secret ${clientSecret} \\
    --api-url ${apiUrl} \\
    --oauth-url ${oauthUrl} \\
    --oauth-scope "${oauthScope}"`;
  }, [data, clientId, clientSecret]);

  const handleTabChange = useCallback((tab: 'json' | 'cli') => {
    setActiveTab(tab);
  }, []);

  return (
    <div className={clsx('flex flex-col space-y-f2', 'max-w-full ')}>
      <div data-testid="api-client-information-section">
        <Toggle
          leftLabel="JSON"
          rightLabel="CLI"
          initialActiveButton="left"
          onLeftClick={() => handleTabChange('json')}
          onRightClick={() => handleTabChange('cli')}
          buttonHeight="h-fit"
          buttonPadding="px-[1ch]"
          width="w-fit"
          inline
        />

        {activeTab === 'json' ? (
          <CodeBlock language="json">{JSON.stringify(data, null, 2)}</CodeBlock>
        ) : (
          <CodeBlock language="bash">{cliLoginCommand}</CodeBlock>
        )}
      </div>
      <ErrorText>clientSecret will not be shown again.</ErrorText>
    </div>
  );
};
